import React, { useEffect, useMemo, useState } from 'react';
import { Button, Container } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import InvitationsTable from '@features/admin/invitations/components/InvitationsTable';
import SendInvitationModal from '@features/admin/invitations/components/SendInvitationModal';
import EntityScopePicker from '@features/entity/components/EntityScopePicker';
import RequirePermission from '@core/components/RequirePermission';
import { ACTION_CODES } from '@features/entity/types/actionCodes';
import { useEntityContext } from '@features/entity/hooks/usePermissions';
import { getApiScopeFromValue } from '@features/invitations/utils/getScopeFromParam';
import { SentInvitationTarget } from '@features/invitations/types/sendInvitationModalProps';
import {
  encodeEntityScopeDestination,
  ENTITY_SCOPE_TOGGLE_CLASS,
  formatEntityScopeLabel,
} from '@features/entity/constants/entityScopePicker';

export interface ActiveScope {
  scope: string;
  scopeRefId: number;
  name?: string;
}

const DistrictInvitesPage: React.FC = () => {
  const { districtId } = useParams<{ districtId: string }>();
  const [showCreate, setShowCreate] = useState(false);
  const sid = Number(districtId);

  const { context } = useEntityContext();
  const ownLabel = useMemo(() => {
    const name = context?.scopeName?.trim() || 'This District';
    return formatEntityScopeLabel('DISTRICT', name.replace(/^District:\s*/i, ''));
  }, [context?.scopeName]);

  const [active, setActive] = useState<ActiveScope>({ scope: 'DISTRICT', scopeRefId: sid });
  const [reloadSignal, setReloadSignal] = useState(0);

  useEffect(() => {
    if (!(sid > 0)) return;
    setActive((prev) =>
      prev.scope === 'DISTRICT' && prev.scopeRefId !== sid
        ? { scope: 'DISTRICT', scopeRefId: sid, name: ownLabel }
        : prev
    );
  }, [sid, ownLabel]);

  const destinationValue = useMemo(
    () => encodeEntityScopeDestination(active.scope, active.scopeRefId),
    [active.scope, active.scopeRefId]
  );

  const handleSent = (target: SentInvitationTarget) => {
    if (
      getApiScopeFromValue(target.scope) === getApiScopeFromValue(active.scope) &&
      Number(target.scopeRefId) === Number(active.scopeRefId)
    ) {
      setReloadSignal((k) => k + 1);
    }
  };

  const handleDestinationChange = (next: ActiveScope) => {
    setActive({
      scope: next.scope,
      scopeRefId: next.scopeRefId,
      name: next.name,
    });
  };

  const scopeSelector =
    sid > 0 ? (
      <EntityScopePicker
        ownScopeValue="D"
        ownScopeRefId={sid}
        ownLabel={ownLabel}
        value={destinationValue}
        onChange={handleDestinationChange}
        readOnlyWhenNoChildren={false}
        toggleClassName={ENTITY_SCOPE_TOGGLE_CLASS}
        testId="district-invite-scope-picker"
        placeholder="Select destination"
      />
    ) : null;

  return (
    <Container fluid className="py-4">
      <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center gap-3 mb-4">
        <div className="ntcog-page-title-block mb-0">
          <h1 className="ntcog-page-heading mb-1">Invitations</h1>
          <p className="text-muted mb-0">Manage invitations for this district and its churches</p>
        </div>
        {sid > 0 && (
          <RequirePermission mode="section" action={ACTION_CODES.SEND_INVITE_DISTRICT} scopeType="DISTRICT" scopeId={sid}>
            <Button variant="primary" onClick={() => setShowCreate(true)}>
              <i className="bi bi-send me-2" aria-hidden="true"></i>
              Invite User
            </Button>
          </RequirePermission>
        )}
      </div>

      <InvitationsTable
        scope={active.scope}
        scopeRefId={active.scopeRefId}
        showPagination={true}
        leadingControl={scopeSelector}
        reloadSignal={reloadSignal}
      />

      <SendInvitationModal
        show={showCreate}
        onHide={() => setShowCreate(false)}
        scope="D"
        scopeRefId={sid > 0 ? sid : undefined}
        scopeName={context?.scopeName}
        destinationScope={active.scope}
        destinationScopeRefId={active.scopeRefId}
        destinationName={active.name ?? ownLabel}
        onDestinationChange={handleDestinationChange}
        onSent={handleSent}
      />
    </Container>
  );
};

export default DistrictInvitesPage;
